import { useEffect, useMemo, useRef, useState } from 'react';
import { CodeEditor } from './components/CodeEditor';
import { DebugControls } from './components/DebugControls';
import { DebugInspector } from './components/DebugInspector';
import { Icon } from './components/Icon';
import { LoadMenu } from './components/LoadMenu';
import { MemoryView } from './components/MemoryView';
import { TestsPanel } from './components/TestsPanel';
import type { DebugSnapshot, MemoryRegion, StepMode } from './lib/debug/types';

import { parseDiagnostics } from './lib/diagnostics';
import { Compiler, RuntimeSession } from './lib/engine';
import { examples } from './lib/examples';
import {
	errorMessage,
	LIMITS,
	type MemoryPage,
	type RunResult,
	type TestCase,
	type TestResult,
} from './lib/protocol';
import { loadProject, type Project, saveProject } from './lib/storage';
import { compareTest } from './lib/test-cases';
import { useTheme } from './lib/theme';

const first = examples[0];
const initialProject: Project = {
	version: 1,
	source: first.source,
	stdin: first.stdin,
	tests: first.tests,
	exampleId: first.id,
};
type Tab = 'console' | 'tests' | 'compiler';
const compilationMessages = {
	idle: 'Not compiled yet. Compile to check your program with Clang -Wall -Wextra.',
	compiling: 'Compiling with Clang -Wall -Wextra…',
	succeeded: 'Compilation succeeded.',
	failed: 'Compilation failed.',
	stopped: 'Compilation stopped.',
};

export function App() {
	const { theme, toggleTheme } = useTheme();
	const [project, setProject] = useState<Project>(initialProject);
	const [ready, setReady] = useState(false);
	const [savingEnabled, setSavingEnabled] = useState(true);

	const [storageError, setStorageError] = useState('');
	const [busy, setBusy] = useState(false);
	const [status, setStatus] = useState('Ready to compile');
	const [error, setError] = useState('');
	const [tab, setTab] = useState<Tab>('console');

	const [diagnostics, setDiagnostics] = useState('');
	const [compilationStatus, setCompilationStatus] =
		useState<keyof typeof compilationMessages>('idle');
	const [stdout, setStdout] = useState('');
	const [stderr, setStderr] = useState('');
	const [result, setResult] = useState<RunResult | null>(null);
	const [memory, setMemory] = useState<MemoryPage | null>(null);
	const [memoryRegion, setMemoryRegion] = useState<MemoryRegion | null>(null);
	const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
	const [debugActive, setDebugActive] = useState(false);
	const [snapshot, setSnapshot] = useState<DebugSnapshot | null>(null);
	const [paused, setPaused] = useState(false);
	const [selectedFrameId, setSelectedFrameId] = useState<number>();
	const [hoveredVariable, setHoveredVariable] = useState<{
		key: string;
		origin: 'editor' | 'inspector' | 'memory';
	} | null>(null);
	const highlightedVariableKey = paused ? (hoveredVariable?.key ?? null) : null;
	function hoverVariable(origin: 'editor' | 'inspector' | 'memory', key: string | null) {
		// A late leave event from one pane must not erase a hover in another pane.
		setHoveredVariable((previous) =>
			key
				? previous?.key === key && previous.origin === origin
					? previous
					: { key, origin }
				: previous?.origin === origin
					? null
					: previous,
		);
	}
	const linkedVariables = useMemo(() => {
		if (!paused || !snapshot) return [];
		const frame =
			snapshot.frames.find((frame) => frame.id === selectedFrameId) ?? snapshot.frames.at(-1);
		return [...(frame?.variables ?? []), ...snapshot.globals];
	}, [paused, snapshot, selectedFrameId]);
	const highlightedVariable = linkedVariables.find(
		(variable) => variable.key === highlightedVariableKey,
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: memory is intentionally untracked to navigate on hover changes only, not on page responses or explicit inspection
	useEffect(() => {
		if (!paused || !highlightedVariable || hoveredVariable?.origin === 'memory' || !memory) return;
		const address = highlightedVariable.address;
		const visibleEnd = address + Math.min(highlightedVariable.byteSize, memory.bytes.length);
		if (
			address < memory.totalBytes &&
			(address < memory.offset || visibleEnd > memory.offset + memory.bytes.length)
		)
			session.current?.inspect(address);
		// Navigate on hover changes only, not on page responses or explicit inspection.
	}, [highlightedVariable, hoveredVariable?.origin, paused]);
	const [playing, setPlaying] = useState(false);
	const [delay, setDelay] = useState(500);
	const [breakpoints, setBreakpoints] = useState<number[]>([]);
	const breakpointLines = useRef(breakpoints);
	breakpointLines.current = breakpoints;
	const debugSupported = crossOriginIsolated && typeof SharedArrayBuffer !== 'undefined';
	const compiler = useRef(new Compiler());
	const session = useRef<RuntimeSession | null>(null);
	const active = useRef<AbortController | null>(null);

	const sourceDiagnostics = parseDiagnostics(diagnostics);

	useEffect(() => {
		let mounted = true;
		loadProject()
			.then((saved) => {
				if (mounted && saved) setProject(saved);
			})
			.catch((error) => {
				if (mounted) {
					setStorageError(errorMessage(error));
					setSavingEnabled(false);
				}
			})
			.finally(() => {
				if (mounted) setReady(true);
			});
		return () => {
			mounted = false;
			active.current?.abort();
			session.current?.dispose();
		};
	}, []);

	useEffect(() => {
		if (!ready || !savingEnabled) return;
		let current = true;

		const timer = setTimeout(() => {
			saveProject(project)
				.then(() => {
					if (current) setStorageError('');
				})
				.catch((error) => {
					if (current) setStorageError(errorMessage(error));
				});
		}, 350);
		return () => {
			current = false;
			clearTimeout(timer);
		};
	}, [project, ready, savingEnabled]);

	function clearExecution() {
		session.current?.dispose();
		session.current = null;
		setMemory(null);
		setMemoryRegion(null);
		setResult(null);
		setStdout('');
		setStderr('');
		setError('');
		setDebugActive(false);
		setSnapshot(null);
		setPaused(false);
		setPlaying(false);
		setHoveredVariable(null);
		setSelectedFrameId(undefined);
	}

	function changeSource(source: string) {
		if (source === project.source) return;
		clearExecution();
		setDiagnostics('');
		setCompilationStatus('idle');
		setTestResults({});
		setBreakpoints([]);
		setStatus('Ready to compile');
		setProject((p) => ({ ...p, source }));
	}

	async function execute(mode: 'compile' | 'run' | 'tests' | 'debug', tests: TestCase[] = []) {
		if (active.current || busy || !ready) return;
		const controller = new AbortController();
		active.current = controller;
		setBusy(true);
		clearExecution();
		setDiagnostics('');
		setCompilationStatus('compiling');
		setDebugActive(mode === 'debug');
		setTab(mode === 'tests' ? 'tests' : mode === 'compile' ? 'compiler' : 'console');
		if (mode === 'tests')
			setTestResults((previous) =>
				Object.fromEntries(
					Object.entries(previous).filter(([id]) => !tests.some((t) => t.id === id)),
				),
			);
		let compilationFinished = false;
		try {
			setStatus('Preparing compiler…');
			const built = await compiler.current.compile(
				project.source,
				controller.signal,
				setStatus,
				mode === 'debug',
			);
			controller.signal.throwIfAborted();
			compilationFinished = true;
			setDiagnostics(built.diagnostics);
			setCompilationStatus(built.module ? 'succeeded' : 'failed');
			if (!built.module) {
				setStatus('Compilation failed');
				setError('Fix the compiler errors before running.');
				setTab('compiler');
				return;
			}
			if (mode === 'compile') {
				setStatus('Compiled successfully');
				return;
			}
			if (mode === 'run' || mode === 'debug') {
				setStatus(mode === 'debug' ? 'Starting debugger…' : 'Running WebAssembly…');
				if (mode === 'debug' && !built.debug)
					throw new Error('Compiler did not return debug metadata.');
				const runtime = new RuntimeSession(setMemory);
				session.current = runtime;
				const test = mode === 'debug' ? tests[0] : undefined;
				const run = await runtime.run(
					built.module,
					test?.stdin ?? project.stdin,
					test?.timeoutMs ?? LIMITS.runMs,
					controller.signal,
					(out, err) => {
						setStdout(out);
						setStderr(err);
					},
					mode === 'debug' && built.debug
						? {
								metadata: built.debug,
								breakpoints: breakpointLines.current,
								onPause: (next) => {
									setSnapshot(next);
									setPaused(true);
									setSelectedFrameId(next.frames.at(-1)?.id);
									setHoveredVariable(null);
									if (next.reason === 'breakpoint') setPlaying(false);
									setStatus(
										`${next.reason === 'breakpoint' ? 'Breakpoint' : 'Paused'} · line ${next.line}`,
									);
								},
								onRunning: () => {
									setPaused(false);
									setHoveredVariable(null);
									setStatus('Debugging · running to checkpoint…');
								},
							}
						: undefined,
				);
				if (test)
					setTestResults((previous) => ({
						...previous,
						[test.id]: compareTest(test, run),
					}));
				setResult(run);
				if (run.memoryBytes === 0) setMemory(null);
				setStatus(
					run.status === 'exited' ? `Finished · exit ${run.exitCode}` : `Execution ${run.status}`,
				);
				if (run.error && run.status !== 'stopped') setError(run.error);
			} else {
				let passed = 0;
				for (const [index, test] of tests.entries()) {
					if (controller.signal.aborted) break;
					setStatus(`Running test ${index + 1}/${tests.length}: ${test.name}`);
					const runtime = new RuntimeSession(() => {});
					session.current = runtime;
					const run = await runtime.run(
						built.module,
						test.stdin,
						test.timeoutMs,
						controller.signal,
						() => {},
					);
					runtime.dispose();
					const checked = compareTest(test, run);
					if (checked.passed) passed++;
					setTestResults((previous) => ({ ...previous, [test.id]: checked }));
				}
				setStatus(
					controller.signal.aborted ? 'Tests stopped' : `${passed}/${tests.length} tests passed`,
				);
			}
		} catch (error) {
			if (!compilationFinished) {
				setCompilationStatus(controller.signal.aborted ? 'stopped' : 'failed');
				if (!controller.signal.aborted) setDiagnostics(errorMessage(error));
			}
			if (controller.signal.aborted) setStatus('Stopped');
			else {
				setError(errorMessage(error));
				setStatus('Could not complete operation');
			}
		} finally {
			active.current = null;
			setBusy(false);
			setDebugActive(false);
			setPaused(false);
			setSnapshot(null);
			setPlaying(false);
			setMemoryRegion(null);
			setHoveredVariable(null);
		}
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: snapshot?.sequence re-arms the timer each checkpoint
	useEffect(() => {
		if (!debugActive || !paused || !playing) return;
		const timer = setTimeout(() => session.current?.resume('into'), delay);
		return () => clearTimeout(timer);
	}, [debugActive, paused, playing, delay, snapshot?.sequence]);

	function step(mode: StepMode) {
		setPlaying(false);
		session.current?.resume(mode);
	}
	function pauseDebug() {
		setPlaying(false);
		session.current?.pause();
	}
	function toggleBreakpoint(line: number) {
		const current = breakpointLines.current;
		const next = current.includes(line) ? current.filter((n) => n !== line) : [...current, line];
		breakpointLines.current = next;
		setBreakpoints(next);
		session.current?.setBreakpoints(next);
	}

	useEffect(() => {
		const keydown = (event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
				event.preventDefault();
				void execute('run');
			}
			if (event.key === 'Escape' && active.current) active.current.abort();
		};
		window.addEventListener('keydown', keydown);
		return () => window.removeEventListener('keydown', keydown);
	});

	function replaceProject(next: Project) {
		clearExecution();
		setDiagnostics('');
		setCompilationStatus('idle');
		setTestResults({});
		setStatus('Ready to compile');
		setBreakpoints([]);
		setProject(next);
	}

	function loadExample(id: string) {
		if (busy || !ready) return;
		const next = examples.find((item) => item.id === id);
		if (!next) return;
		if (
			!window.confirm(
				'Replace the current source, input, and tests with this example? Export your source first if you want to keep it.',
			)
		)
			return;
		replaceProject({
			version: 1,
			source: next.source,
			stdin: next.stdin,
			tests: next.tests,
			exampleId: next.id,
		});
	}

	async function loadFile(file: File) {
		if (busy || !ready) return;
		if (file.size > LIMITS.sourceBytes) {
			setError('Source files must be 128 KiB or smaller.');
			return;
		}
		if (
			!window.confirm(
				'Replace the current source with this file and clear the input and tests? Export your source first if you want to keep it.',
			)
		)
			return;
		setBusy(true);
		try {
			const source = await file.text();
			if (new TextEncoder().encode(source).length > LIMITS.sourceBytes)
				throw new Error('Source files must be 128 KiB or smaller.');
			replaceProject({
				version: 1,
				source,
				stdin: '',
				tests: [],
				exampleId: '',
			});
		} catch (error) {
			setError(`Could not load file: ${errorMessage(error)}`);
		} finally {
			setBusy(false);
		}
	}

	function inspectMemory(offset: number, region?: MemoryRegion | null) {
		if (!memory || !Number.isInteger(offset) || offset < 0 || offset >= memory.totalBytes) {
			setError('That address is outside the program’s allocated linear memory.');
			return;
		}
		setMemoryRegion(region ?? null);
		session.current?.inspect(offset);
	}

	function exportSource() {
		const url = URL.createObjectURL(new Blob([project.source], { type: 'text/x-c' }));
		const link = document.createElement('a');
		link.href = url;
		link.download = 'main.c';
		link.click();
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}

	return (
		<div className="app-shell">
			<main>
				{storageError && (
					<div className="notice warning" role="alert">
						<span className="notice-message">
							Local save unavailable: {storageError}{' '}
							<button type="button" onClick={exportSource}>
								Export main.c
							</button>
						</span>
						<button
							type="button"
							className="icon-button notice-dismiss"
							onClick={() => setStorageError('')}
							aria-label="Dismiss save warning"
							title="Dismiss"
						>
							<Icon name="close" size={14} />
						</button>
					</div>
				)}
				{error && (
					<div className="notice error" role="alert">
						<span className="notice-message">{error}</span>
						<button
							type="button"
							className="icon-button notice-dismiss"
							onClick={() => setError('')}
							aria-label="Dismiss error"
							title="Dismiss"
						>
							<Icon name="close" size={14} />
						</button>
					</div>
				)}
				<div className="workspace-grid">
					<div className="code-column">
						<section className="panel editor-panel" aria-label="Source editor">
							<div className="panel-heading editor-toolbar">
								<LoadMenu
									disabled={busy || !ready}
									onFile={(file) => void loadFile(file)}
									onExample={loadExample}
								/>
								<div className="execution-controls">
									<button
										type="button"
										disabled={busy || !ready}
										onClick={() => void execute('compile')}
									>
										<Icon name="hammer" size={13} /> Compile
									</button>
									<button
										type="button"
										className="primary"
										disabled={busy || !ready}
										onClick={() => void execute('run')}
										title="Ctrl/⌘ + Enter"
									>
										<span aria-hidden="true">▶</span> Run
									</button>
									<button
										type="button"
										disabled={busy || !ready || !debugSupported}
										onClick={() => void execute('debug')}
										title={
											debugSupported
												? 'Experimental source debugger · click the editor gutter to set breakpoints'
												: 'Debugging requires COOP/COEP cross-origin-isolation headers'
										}
									>
										<Icon name="bug" size={13} /> Debug
									</button>
									<button
										type="button"
										className={busy ? 'stop-button' : ''}
										disabled={!busy}
										onClick={() => active.current?.abort()}
									>
										■ Stop
									</button>
									<button
										type="button"
										className="icon-button export-button"
										onClick={exportSource}
										disabled={!ready}
										aria-label="Export main.c"
										title="Export main.c"
									>
										<Icon name="download" size={14} />
									</button>
									<button
										type="button"
										className="icon-button"
										onClick={toggleTheme}
										aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
										title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
									>
										<Icon name={theme === 'dark' ? 'sun' : 'moon'} size={14} />
									</button>
								</div>
							</div>

							{debugActive && (
								<DebugControls
									paused={paused}
									playing={playing}
									delay={delay}
									line={snapshot?.line}
									onStep={step}
									onPlay={() => setPlaying(true)}
									onPause={pauseDebug}
									onDelay={setDelay}
								/>
							)}
							<CodeEditor
								theme={theme}
								source={project.source}
								onChange={changeSource}
								diagnostics={sourceDiagnostics}
								readOnly={busy || !ready}
								currentLine={paused ? snapshot?.line : undefined}
								breakpoints={breakpoints}
								onToggleBreakpoint={toggleBreakpoint}
								debugVariables={linkedVariables}
								highlightedVariableKey={highlightedVariableKey}
								onHoverVariable={(key) => hoverVariable('editor', key)}
							/>
						</section>
						<section className="panel output-panel" aria-label="Program output and tests">
							<div className="tab-list" role="tablist" aria-label="Output panels">
								{(['console', 'tests', 'compiler'] as Tab[]).map((item) => (
									<button
										type="button"
										key={item}
										role="tab"
										id={`tab-${item}`}
										aria-controls={`panel-${item}`}
										aria-selected={tab === item}
										onClick={() => setTab(item)}
									>
										{item === 'console' && <Icon name="terminal" size={13} />}
										{item === 'console'
											? 'Console'
											: item === 'tests'
												? `Tests (${project.tests.length})`
												: `Compiler${sourceDiagnostics.length ? ` (${sourceDiagnostics.length})` : ''}`}
									</button>
								))}
								<span className="tab-meta" role="status" aria-live="polite" title={status}>
									<span className={`status-dot ${busy ? 'working' : ''}`} />
									<span className="status-text">{status}</span>
								</span>
							</div>
							<div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
								{tab === 'console' && (
									<div className="console-layout">
										<div className="stdin-section">
											<label htmlFor="stdin">
												STANDARD INPUT <span>Available before execution; EOF after this text.</span>
											</label>
											<textarea
												id="stdin"
												spellCheck={false}
												value={project.stdin}
												disabled={busy || !ready}
												onChange={(e) => setProject((p) => ({ ...p, stdin: e.target.value }))}
												placeholder="Input for scanf, fgets, getchar…"
											/>
										</div>
										<div className="stdout-section">
											<div className="output-heading">
												<span>STANDARD OUTPUT</span>
												{result && (
													<span
														className={
															result.status === 'exited' && result.exitCode === 0
																? 'success-text'
																: 'danger-text'
														}
													>
														{result.status === 'exited' ? `exit ${result.exitCode}` : result.status}
													</span>
												)}
											</div>
											<pre data-testid="stdout" className={!stdout ? 'muted' : ''}>
												{stdout ||
													(result ? '(no output)' : 'Your program’s output will appear here.')}
											</pre>
											{stderr && (
												<>
													<h3>STANDARD ERROR</h3>
													<pre className="stderr" data-testid="stderr">
														{stderr}
													</pre>
												</>
											)}
										</div>
									</div>
								)}
								{tab === 'compiler' && (
									<pre className="compiler-output" data-testid="compiler-output">
										<span
											className={
												compilationStatus === 'succeeded'
													? 'success-text'
													: compilationStatus === 'failed'
														? 'danger-text'
														: undefined
											}
										>
											{compilationMessages[compilationStatus]}
										</span>
										{diagnostics.trim()
											? `\n\n${diagnostics}`
											: compilationStatus === 'succeeded'
												? '\nNo warnings or errors.'
												: ''}
									</pre>
								)}
								{tab === 'tests' && (
									<TestsPanel
										tests={project.tests}
										results={testResults}
										busy={busy || !ready}
										onChange={(tests) => {
											setProject((p) => ({ ...p, tests }));
											setTestResults({});
										}}
										onRun={(tests) => void execute('tests', tests)}
										debugSupported={debugSupported}
										onDebug={(test) => {
											setProject((p) => ({ ...p, stdin: test.stdin }));
											void execute('debug', [test]);
										}}
										onUseInput={(stdin) => {
											setProject((p) => ({ ...p, stdin }));
											setTab('console');
										}}
									/>
								)}
							</div>
						</section>
					</div>
					<aside className="inspector-column">
						{debugActive && (
							<DebugInspector
								snapshot={snapshot}
								paused={paused}
								onInspect={inspectMemory}
								frameId={selectedFrameId}
								onSelectFrame={(id) => {
									setSelectedFrameId(id);
									setHoveredVariable(null);
								}}
								highlightedVariableKey={highlightedVariableKey}
								revealHighlight={hoveredVariable?.origin === 'editor'}
								onHoverVariable={(key) => hoverVariable('inspector', key)}
							/>
						)}
						<MemoryView
							memory={memory}
							mode={debugActive ? (paused ? 'paused' : 'running') : 'finished'}
							onInspect={inspectMemory}
							region={memoryRegion}
							onSelectRegion={setMemoryRegion}
							debugVariables={linkedVariables}
							highlightedVariable={highlightedVariable}
							onHoverVariable={(key) => hoverVariable('memory', key)}
							accesses={snapshot?.accesses}
							accessesTruncated={snapshot?.accessesTruncated}
						/>
					</aside>
				</div>
			</main>
		</div>
	);
}
